<template>
    <v-dialog
        v-if="isB2B"
        v-model="isShowDialog"
        persistant
        :fullscreen="!!$breakpoint.smAndDown"
        :width="$breakpoint.smAndDown ? '100%' : '40%'"
        max-width="1000"
        @click:outside="clearInput"
    >
        <template v-slot:activator="{props}">
            <div
                v-ripple
                style="min-width: 90px"
                class="d-flex flex-column align-center justify-center cursor-pointer px-4"
                v-bind="props"
                @click="markMessagesRead"
            >
                <v-badge color="green" :content="count" class="badge full-width" floating>
                    <div class="d-flex flex-column align-center full-height mx-auto">
                        <v-icon class="mb-1"> mdi-chat-outline </v-icon>
                        <span class="text-uppercase font-weight-medium" style="font-size: 8px; margin-top: 2px">
                            {{ $t(`chat`) }}
                        </span>
                    </div>
                </v-badge>
            </div>
        </template>
        <template v-slot>
            <v-card>
                <v-card-actions class="justify-end px-5 pt-5 pb-0">
                    <div class="text-h4 text-primary ma-auto">{{ $t('chat') }} {{ $t('order') }} #{{ orderId }}</div>
                    <v-icon class="v-card-close" @click="isShowDialog = false">mdi-close</v-icon>
                </v-card-actions>
                <div class="wrapper pa-5">
                    <div ref="messageWindow" class="message-box ma-1 pa-1 rounded bg-grey-lighten-4">
                        <v-card
                            v-for="message in messages"
                            :key="message.messageDateTime + message.userId + message.messageText"
                            class="mt-1 rounded-lg"
                            :class="{'text-right': message.userId === personId}"
                        >
                            <v-list-item lines="two">
                                <v-list-item-title>
                                    <v-icon size="small">mdi-account</v-icon>
                                    <span :class="{'text-primary': message.userId === personId}">
                                        {{ message.senderFirstName }}
                                        {{ message.senderLastName }}
                                    </span>
                                    {{ message.messageDateTime }}
                                </v-list-item-title>
                                <v-list-item-subtitle class="ml-5 make-new-line">
                                    {{ message.messageText }}
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-card>
                    </div>
                    <div class="mt-2 d-flex text-no-wrap align-end">
                        <div class="textarea mr-5">
                            <v-textarea
                                v-model="message"
                                variant="outlined"
                                rows="2"
                                :placeholder="$t('enter_message')"
                                :label="$t('enter_message')"
                                hide-details="auto"
                            />
                        </div>
                        <div class="d-flex flex-column justify-center align-center button-send">
                            <v-btn
                                variant="flat"
                                block
                                :disabled="!message.length"
                                size="large"
                                color="primary"
                                @click="sendMessage()"
                            >
                                {{ $t('send') }}
                            </v-btn>
                        </div>
                    </div>
                </div>
            </v-card>
        </template>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'

    @Component
    export class ChatModal extends Vue {
        @Prop({required: true}) orderId

        messages = []
        message = ''
        count = 0

        isShowDialog = false

        async mounted() {
            if (this.isB2B) {
                await this.getMessages()
                this.countUnreadMessages()
            }
        }

        countUnreadMessages() {
            this.messages.forEach(message => {
                if (!message.messageRead) {
                    this.count++
                }
            })
        }

        async sendMessage() {
            try {
                await this.$api.message.put(this.orderId, {messageText: this.message})
            } catch (e) {
                console.error('sendMessage failed', e.message)
            }

            this.clearInput()
            await this.getMessages()
            this.scrollToLastMessage()
            await this.markMessagesRead()
        }

        async markMessagesRead() {
            try {
                await this.$api.markReadCount.put(this.orderId, {markReadMessagesCount: this.messages.length + 1})
                // eslint-disable-next-line no-empty
            } catch (e) {}
            this.count = 0
            this.scrollToLastMessage()
        }

        scrollToLastMessage() {
            this.$refs.messageWindow.scrollTop = this.$refs.messageWindow.scrollHeight
        }

        clearInput() {
            this.message = ''
        }

        async getMessages() {
            try {
                const messages = await this.$api.messages.get(this.orderId)
                this.messages = messages.messages.reverse()
            } catch (e) {
                this.messages = []
            }
        }

        get personId() {
            return authStore.person.personId
        }

        get isB2B() {
            return authStore.isB2B
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default ChatModal
</script>

<style lang="scss" scoped>
    @use 'vuetify/settings';
    @use 'sass:map';

    .message-box {
        min-height: 100px;
        max-height: 400px;
        overflow-y: auto;
    }

    @media #{map.get(settings.$display-breakpoints, 'sm-and-down')} {
        .wrapper {
            display: flex;
            flex-direction: column;
            min-height: 90vh;
        }

        .message-box {
            min-height: 75vh;
            max-height: 75vh;
        }
    }

    .make-new-line {
        white-space: pre-line;
        -webkit-line-clamp: unset;
    }

    .textarea {
        flex: 0.8 1 auto;
    }

    .button-send {
        flex: 0.2 1 auto;
    }

    :deep(.v-btn--fixed.v-btn--bottom) {
        bottom: 48px !important;
    }
</style>
